import React, { useState } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  useToast,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Switch,
  FormErrorMessage,
  Alert,
  AlertIcon,
  Code,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import { ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const CreateTemplate = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    template_type: 'page',
    layout_structure: '',
    styles: '',
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const toast = useToast();
  const navigate = useNavigate();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (!formData.template_type) {
      newErrors.template_type = 'Template type is required';
    }

    // Validate JSON if provided
    if (formData.layout_structure?.trim()) {
      try {
        JSON.parse(formData.layout_structure);
      } catch (e) {
        newErrors.layout_structure = 'Invalid JSON format';
      }
    }

    if (formData.styles?.trim()) {
      try {
        JSON.parse(formData.styles);
      } catch (e) {
        newErrors.styles = 'Invalid JSON format';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        ...formData,
        layout_structure: formData.layout_structure?.trim() ? JSON.parse(formData.layout_structure) : null,
        styles: formData.styles?.trim() ? JSON.parse(formData.styles) : null
      };

      const response = await apiClient(`/templates/${tenantId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData)
      });

      if (response.error) {
        throw new Error(response.error);
      }

      toast({
        title: 'Template created successfully',
        description: `Template "${formData.name}" has been created`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      navigate('/templates');
    } catch (error) {
      console.error('Error creating template:', error);
      toast({
        title: 'Error creating template',
        description: error.message || 'Failed to create template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors({ ...errors, [field]: null });
    }
  };

  const getDefaultLayoutStructure = (type) => {
    const structures = {
      page: {
        sections: [
          { type: 'header', components: ['navigation', 'logo'] },
          { type: 'main', components: ['content'] },
          { type: 'footer', components: ['links', 'copyright'] }
        ]
      },
      component: {
        type: 'component',
        props: ['title', 'content', 'variant'],
        slots: ['default']
      },
      layout: {
        grid: { columns: 12, rows: 'auto' },
        areas: ['header', 'sidebar', 'main', 'footer']
      },
      email: {
        sections: [
          { type: 'header', style: 'email-header' },
          { type: 'body', style: 'email-body' },
          { type: 'footer', style: 'email-footer' }
        ]
      }
    };
    
    return JSON.stringify(structures[type] || structures.page, null, 2);
  };

  const getDefaultStyles = (type) => {
    const styles = {
      page: {
        header: { backgroundColor: '#ffffff', padding: '1rem' },
        main: { minHeight: '70vh', padding: '2rem' },
        footer: { backgroundColor: '#f8f9fa', padding: '1rem' }
      },
      component: {
        container: { padding: '1rem', border: '1px solid #e2e8f0' },
        title: { fontSize: '1.5rem', fontWeight: 'bold' }
      },
      layout: {
        grid: { gap: '1rem' },
        sidebar: { width: '250px' },
        main: { flex: 1 }
      },
      email: {
        container: { fontFamily: 'Arial, sans-serif' },
        header: { backgroundColor: '#007bff', color: 'white' },
        body: { padding: '20px' }
      }
    };
    
    return JSON.stringify(styles[type] || styles.page, null, 2);
  };

  const handleTypeChange = (type) => {
    setFormData({
      ...formData,
      template_type: type,
      layout_structure: getDefaultLayoutStructure(type),
      styles: getDefaultStyles(type)
    });
  };

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.lg" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/templates">Templates</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Create Template</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <form onSubmit={handleSubmit}>
        <VStack spacing={6} align="stretch">
          <HStack justify="space-between">
            <Box>
              <Heading size="lg" mb={2}>Create New Template</Heading>
              <Text color="gray.500">
                Create a reusable template for your content
              </Text>
            </Box>
            <HStack>
              <Button 
                variant="outline" 
                onClick={() => navigate('/templates')}
                isDisabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                colorScheme="blue"
                isLoading={loading}
                loadingText="Creating..."
              >
                Create Template
              </Button>
            </HStack>
          </HStack>

          {/* Basic Information */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Basic Information</Heading>
            </CardHeader>
            <CardBody>
              <VStack spacing={4}>
                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>Template Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="My Template"
                    isDisabled={loading}
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.template_type}>
                  <FormLabel>Template Type</FormLabel>
                  <Select
                    value={formData.template_type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                    isDisabled={loading}
                  >
                    <option value="page">Page Template</option>
                    <option value="component">Component Template</option>
                    <option value="layout">Layout Template</option>
                    <option value="email">Email Template</option>
                  </Select>
                  <FormErrorMessage>{errors.template_type}</FormErrorMessage>
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    {formData.template_type === 'page' && 'Full page templates with header, content, and footer'}
                    {formData.template_type === 'component' && 'Reusable component templates'}
                    {formData.template_type === 'layout' && 'Grid-based layout templates'}
                    {formData.template_type === 'email' && 'Email newsletter and notification templates'}
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Describe this template..."
                    rows={3}
                    isDisabled={loading}
                  />
                </FormControl>

                <FormControl display="flex" alignItems="center">
                  <FormLabel mb="0">
                    Active Template
                  </FormLabel>
                  <Switch
                    isChecked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    isDisabled={loading}
                  />
                  <Text fontSize="sm" color="gray.500" ml={3}>
                    Active templates can be used by pages
                  </Text>
                </FormControl>
              </VStack>
            </CardBody>
          </Card>

          {/* Template Configuration */}
          <Card bg={cardBg} borderColor={borderColor} variant="outline">
            <CardHeader>
              <Heading size="md">Template Configuration</Heading>
            </CardHeader>
            <CardBody>
              <Tabs>
                <TabList>
                  <Tab>Layout Structure</Tab>
                  <Tab>Styles</Tab>
                </TabList>

                <TabPanels>
                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          Define the layout structure in JSON format
                        </Text>
                        <Code fontSize="xs" p={2} borderRadius="md" display="block" whiteSpace="pre">
                          Example for {formData.template_type} template
                        </Code>
                      </Box>
                      
                      <FormControl isInvalid={!!errors.layout_structure}>
                        <Textarea
                          value={formData.layout_structure}
                          onChange={(e) => handleInputChange('layout_structure', e.target.value)}
                          placeholder="Enter JSON layout structure..."
                          rows={12}
                          fontFamily="mono"
                          fontSize="sm"
                          isDisabled={loading}
                        />
                        <FormErrorMessage>{errors.layout_structure}</FormErrorMessage>
                      </FormControl>
                    </VStack>
                  </TabPanel>

                  <TabPanel px={0}>
                    <VStack spacing={4} align="stretch">
                      <Box>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          Define the default styles in JSON format
                        </Text>
                        <Code fontSize="xs" p={2} borderRadius="md" display="block" whiteSpace="pre">
                          CSS-in-JS object notation
                        </Code>
                      </Box>
                      
                      <FormControl isInvalid={!!errors.styles}>
                        <Textarea
                          value={formData.styles}
                          onChange={(e) => handleInputChange('styles', e.target.value)}
                          placeholder="Enter JSON styles..."
                          rows={12}
                          fontFamily="mono"
                          fontSize="sm"
                          isDisabled={loading}
                        />
                        <FormErrorMessage>{errors.styles}</FormErrorMessage>
                      </FormControl>
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </CardBody>
          </Card>
        </VStack>
      </form>
    </Container>
  );
};

export default CreateTemplate;